import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { HiOutlineCalendar } from "react-icons/hi";

import NoTranslate from "../../../atoms/NoTranslate";
import type { AppointmentSummary } from "../../../../hooks/agenda";
import AppointmentModal from "./AppointmentModal";
import { PaymentBadge } from "./AppointmentPayment";
import { paymentViewOf } from "./agendaPayment";
import { endLabel, rangeLabel } from "./agendaDates";

/** An appointment as the AI search lists it: the summary of the agenda, without its organizer. */
export type AiAppointment = Omit<AppointmentSummary, "organizer">;

export type AppointmentQueryResult = {
  type: "appointment_query";
  appointment_filters?: Record<string, unknown>;
  appointments?: AiAppointment[];
  /** Before the list is cut to its limit. */
  count?: number;
  from?: number;
  to?: number;
  error?: string;
};

const K = "dashboard.askAi";

/** The appointments the AI search found, each opening in its window as everywhere else. */
export const AppointmentQueryView: React.FC<{
  result: AppointmentQueryResult;
}> = ({ result }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState<AppointmentSummary | null>(null);
  const [goneIds, setGoneIds] = useState<string[]>([]);
  const [changed, setChanged] = useState<Record<string, AiAppointment>>({});

  if (result.error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {t(
          result.error === "forbidden"
            ? `${K}.appointmentsForbidden`
            : `${K}.errorGeneric`
        )}
      </div>
    );
  }

  const items = (result.appointments ?? [])
    .filter((appointment) => !goneIds.includes(appointment.id))
    .map((appointment) => changed[appointment.id] ?? appointment);
  const count = (result.count ?? items.length) - goneIds.length;
  const first = result.from !== undefined ? moment(result.from) : null;
  const last = result.to !== undefined ? moment(result.to - 1) : null;
  const oneDay = first && last && first.isSame(last, "day");
  const range =
    first && last
      ? rangeLabel(t, "date", first.format("ll"), last.format("ll"))
      : "";
  const heading = oneDay
    ? count > 0
      ? t(`${K}.appointmentsOnDay`, { count, day: first.format("ll") })
      : t(`${K}.appointmentsNoneOnDay`, { day: first.format("ll") })
    : count > 0
    ? t(`${K}.appointmentsFound`, { count, range })
    : t(`${K}.appointmentsNone`, { range });
  const more = count - items.length;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3">
        <HiOutlineCalendar
          className="shrink-0 text-blue-600"
          size={20}
          aria-hidden="true"
        />
        <p className="text-sm font-semibold text-gray-800">{heading.trim()}</p>
      </div>
      {items.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {items.map((appointment) => (
            <li key={appointment.id}>
              <button
                type="button"
                onClick={() => setOpened({ ...appointment, organizer: "" })}
                className="flex w-full flex-col gap-1 px-4 py-2.5 text-left transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <span className="min-w-0">
                  <span className="block break-words text-sm font-medium text-gray-900">
                    <NoTranslate>{appointment.title}</NoTranslate>
                  </span>
                  <span className="block text-xs text-gray-500">
                    {moment(appointment.start).format("ll")} ·{" "}
                    {rangeLabel(
                      t,
                      "time",
                      moment(appointment.start).format("LT"),
                      endLabel(appointment)
                    )}
                  </span>
                  {appointment.participants.length > 0 && (
                    <span className="block break-words text-xs text-gray-500">
                      <NoTranslate>
                        {appointment.participants
                          .map((p) => p.name || p.user_identification)
                          .join(", ")}
                      </NoTranslate>
                    </span>
                  )}
                </span>
                <span className="shrink-0 self-start sm:self-auto">
                  <PaymentBadge view={paymentViewOf(appointment)} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {more > 0 && (
        <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
          {t(`${K}.appointmentsMore`, { count: more })}
        </p>
      )}
      <AppointmentModal
        show={opened !== null}
        onClose={() => setOpened(null)}
        appointment={opened}
        onGone={(id) =>
          setGoneIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
        }
        onChanged={(appointment) =>
          setChanged((rows) => ({ ...rows, [appointment.id]: appointment }))
        }
      />
    </div>
  );
};

export default AppointmentQueryView;
